import React, { useState, useEffect } from "react";
import { useMatch, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import store from "../../reduxstore/reduxStore";
import { LoadingAnimation } from "@texada/ui";
import i18n from "i18n/i18n";
import fleetlogicAPI from "helpers/fleetlogicAPI";
import { ReduxState } from "helpers/helper";
import {
  APP_BASE_PATH,
  APP_DEFAULT_REDIRECT_URL,
  TIS_SESSION_VERIFY_URL
} from "constants/AppConstants";
import { logout } from "../../actions";
import LoginForm from "./LoginForm";
import VerifyLoginUser from "./VerifyLoginUser";
import SRMSyncLoader from "./SRMSyncLoader";
import MobileLogin from "./MobileLogin";
import LoginContext from "./LoginContext";
import LoginInfoDisplay from "./LoginInfoDisplay";
import {
  useLogin,
  switchInstance,
  getLoginError,
  openSrmManagement,
  LoginSource,
  getSelectedLoginTo
} from "./loginUtils";
import MainScreen from "./MainScreen";
import DocDivisionAccessModal from "./DocDivisionAccessModal";

const Login = () => {
  const location = useLocation();
  const { tenantId } = useParams();
  const user = useSelector(state => state.auth.user);
  const query = new URLSearchParams(location.search);
  const [isSyncingData, setIsSyncingData] = useState(false);
  const isMobileLogin = !!useMatch("/mobile/:token");
  const isTisVerify = !!useMatch("/verify");
  const { error, login, loginAuth, setError } = useLogin(setIsSyncingData);
  const isLoggedIn = useSelector<ReduxState, boolean[]>(
    state => state.auth.isAuthenticated
  );
  const [isDocDivisionModalOpen, setIsDocDivisionModalOpen] = useState(false);
  const [isValidatingTis, setIsValidatingTis] = useState(false);

  useEffect(() => {
    if (query.get("callbackUrl")) {
      localStorage.setItem("mobileRedirectUrl", query.get("callbackUrl"));
    }
  }, []);

  useEffect(() => {
    if (!isDocDivisionModalOpen && !isMobileLogin && !tenantId) {
      if (isLoggedIn) {
        if (query.get("callbackUrl")) {
          store.dispatch(logout());
          return;
        }
        if (
          query.get("applicationId") &&
          getSelectedLoginTo(query.get("applicationId")) === LoginSource.SRM
        ) {
          openSrmManagement(user?.compperms?.operator_by_division);
          return;
        }
        if (query.get("switchInstance")) {
          return;
        }
        getAppDefaultUrl();
        return;
      }
      if (query.get("switchInstance")) {
        return;
      }
      if (!isTisVerify) {
        setIsValidatingTis(true);
        verifyTisSession();
      }
    }
  }, [isLoggedIn, isDocDivisionModalOpen]);

  useEffect(() => {
    if (!isLoggedIn || !tenantId) return;
    onSwitchInstance();
  }, [isLoggedIn, tenantId]);

  const onSwitchInstance = async () => {
    const jwtToken = localStorage.getItem("jwtToken");
    if (!jwtToken) {
      window.location.href = APP_DEFAULT_REDIRECT_URL;
      return;
    }
    const userData = jwtDecode(jwtToken);

    if (query.get("email") && query.get("email") !== userData.email) {
      await store.dispatch(logout());
      window.location.href = `${APP_BASE_PATH}/company/${tenantId}?email=${query.get(
        "email"
      )}`;
      return;
    }

    if (userData.tis_session) {
      if (userData.company_upgraded) {
        await switchInstance(user.id, user?.userinstance?.current.id, tenantId);
      }
      if (
        user?.compperms?.login_source === LoginSource.SRM &&
        !user?.is_gateway_admin &&
        !user?.enable_gateway
      ) {
        localStorage.removeItem("verified");
        verifyTisSession();
        return;
      }
      try {
        const redirect = await loginAuth(
          tenantId,
          setIsDocDivisionModalOpen,
          undefined,
          userData.company_sso_enabled,
          userData.email
        );
        localStorage.setItem("verified", true);
        if (redirect) {
          window.location.href = APP_DEFAULT_REDIRECT_URL;
        }
      } catch (e) {
        console.error(e);
        await store.dispatch(logout());
        window.location.href = `${APP_BASE_PATH}/company/${tenantId}?email=${query.get(
          "email"
        )}`;
        return;
      }
      return;
    }
    window.location.href = `${APP_BASE_PATH}/mobile/${jwtToken}`;
  };

  const verifyTisSession = async () => {
    try {
      const tokenRes = await fleetlogicAPI.post(`${TIS_SESSION_VERIFY_URL}/`, {
        internal: true,
        source: "auto_verify"
      });
      window.location.href = `${window.location.origin}${APP_BASE_PATH}/verify?tenant=${
        tokenRes.data.tenant_id
      }&applicationId=${query.get("applicationId") || ""}`;
    } catch (err) {
      setIsValidatingTis(false);
    }
  };

  const getAppDefaultUrl = () => {
    if (
      user?.compperms?.login_source === LoginSource.SRM &&
      !user?.is_gateway_admin &&
      !user?.enable_gateway
    ) {
      localStorage.removeItem("verified");
      if (user.is_tis && user.company_sso_enabled) {
        verifyTisSession();
        return;
      }
      window.location.href = `${window.location.origin}${APP_BASE_PATH}/verify?tenant=${
        user.tenant_id
      }&applicationId=${query.get("applicationId") || ""}`;
      return;
    }
    window.location.href = APP_DEFAULT_REDIRECT_URL;
  };

  if (isLoggedIn && tenantId) {
    const errorToDisplay = getLoginError(error);
    return (
      <MainScreen>
        {errorToDisplay && <LoginInfoDisplay errorMsg={errorToDisplay} />}
        <SRMSyncLoader message={i18n.t("switching_instance_please_wait")} />
        <DocDivisionAccessModal
          isOpen={isDocDivisionModalOpen}
          onClose={() => {
            setIsDocDivisionModalOpen(false);
            getAppDefaultUrl();
          }}
        />
      </MainScreen>
    );
  }

  return (
    <LoginContext.Provider
      value={{
        login,
        error,
        loginAuth,
        setError
      }}
    >
      <div
        className="login-loading"
        style={{ zIndex: isValidatingTis ? 1 : -1 }}
      >
        <LoadingAnimation delay={1000} />
      </div>
      <MainScreen>
        {() => {
          if (isMobileLogin) return <MobileLogin />;
          if (!error && isSyncingData) return <SRMSyncLoader />;
          return isTisVerify ? <VerifyLoginUser /> : <LoginForm />;
        }}
      </MainScreen>
    </LoginContext.Provider>
  );
};

export default Login;
